/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { PromptScreen } from '@atlassian/generative-ai-modal/screens/Prompt';

import type { AIPlugin } from '../../../../aiPluginType';
import {
	resetSessionState,
	startFreeGenStream,
} from '../../../../pm-plugins/ai-session-context/commands';
import { rovoAgentsPluginKey } from '../../../../pm-plugins/rovo-agents/plugin-key';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { RovoPromptScreenWithLogic } from '../../ExperienceApplication/screens-with-logic/RovoPromptScreenWithLogic';
import { ModalRegionErrorBoundary } from '../../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { ModalRegion } from '../../ModalRegion/ModalRegion';
import type { Action } from '../hooks/useUIState';
import { useUserDiscard } from '../hooks/useUserDiscard';
import { configItemUsesFreeGen } from '../utils/checkMigrationStatus';

// Ensures the div wrapping the prompt screen does not alter layout
const promptScreenCSS = css({ display: 'contents' });

function findModalDecoration(modalDecorationSet?: DecorationSet): Decoration | undefined {
	if (!modalDecorationSet) {
		return undefined;
	}

	return (
		modalDecorationSet
			.find(
				undefined,
				undefined,
				(spec) =>
					spec.key === 'modalMountPointDecoration' || spec.key === 'modalMountPointRangeDecoration',
			)
			.shift() ||
		modalDecorationSet
			.find(undefined, undefined, (spec) => spec.key === 'ai-nodeDecoration')
			.shift()
	);
}

export function PromptSessionScreen({
	api,
	dispatch,
	appearance,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	appearance: EditorAppearance;
	dispatch: React.Dispatch<Action>;
}): JSX.Element | null {
	const {
		editorView,
		wrapperElement,
		isRovoLoading,
		endExperience,
		editorPluginAIProvider,
		isInDocumentStreamingEnabled,
	} = useContentComponentContext();
	const {
		lastTriggeredFrom,
		modalMountedTimeStamp,
		modalDecorationSet,
		nextSessionId,
		configItem,
		inlineRovoStreamingTargetId,
		isStreaming,
	} = useSharedPluginStateWithSelector(api, ['aiExperience'], ({ aiExperienceState }) => ({
		lastTriggeredFrom: aiExperienceState?.aiDecorationExperiencePluginState.lastTriggeredFrom,
		modalMountedTimeStamp:
			aiExperienceState?.aiDecorationExperiencePluginState.modalMountedTimeStamp,
		modalDecorationSet: aiExperienceState?.aiDecorationExperiencePluginState.modalDecorationSet,
		nextSessionId: aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
		configItem: aiExperienceState?.aiDecorationExperiencePluginState.configItem,
		inlineRovoStreamingTargetId:
			aiExperienceState?.aiDecorationExperiencePluginState.inlineRovoStreamingTargetId,
		// Reads whichever streaming plugin is registered (legacy or xstate migration).
		isStreaming: Boolean(aiExperienceState?.isStreaming),
	}));

	const isDirectionalPinningEnabled = useMemo(() => {
		return !isRovoLoading;
	}, [isRovoLoading]);

	const promptScreenRef = useRef<HTMLDivElement>(null);

	// State for fallback free-gen prompt input (used when onSubmit is not available)
	const [textInput, setTextInput] = useState('');
	const textInputRef = useRef(textInput);
	textInputRef.current = textInput;

	const cancel = useCallback(
		(options?: {
			shouldClearSelectionContext?: boolean;
			shouldIgnoreNextSpaceTrigger?: boolean;
		}) => {
			endExperience({
				shouldClearSelectionContext: options?.shouldClearSelectionContext,
				shouldIgnoreNextSpaceTrigger: options?.shouldIgnoreNextSpaceTrigger,
			});
			api?.core?.actions.execute(resetSessionState());
			dispatch({ type: 'terminate' });
		},
		[endExperience, api?.core?.actions, dispatch],
	);

	// Fallback free-gen submit handler — dispatches START_FREE_GEN_STREAM
	// to trigger streaming via the ProseMirror plugin for products without Rovo.
	// Uses ref to always get the latest textInput, avoiding stale closure issues
	// when the submit callback is captured inside the memoized PromptEditor preset.
	const submit = useCallback(() => {
		const currentInput = textInputRef.current;
		if (!currentInput?.trim()) {
			return;
		}
		api?.core?.actions.execute(startFreeGenStream(currentInput));
	}, [api?.core?.actions]);

	const onTextChange = useCallback((input: string) => {
		setTextInput(input);
	}, []);

	// Callbacks for the openInRovoChat flow (SmartLink Rovo button). Hoisted before
	// early returns to satisfy React hooks rules and avoid inline object recreation.
	const rovoChatAIFeatureContext = configItem?.rovoChatAIFeatureContext;
	const rovoChatInteractionSource = configItem?.rovoChatInteractionSource;

	const publishChatNew = useCallback(
		(prompt: DocNode) => {
			const publish = rovoAgentsPluginKey.getState(editorView.state)?.publish;
			if (publish) {
				const payload: Parameters<typeof publish>[0] = {
					type: 'chat-new',
					product: editorPluginAIProvider.product,
					data: {
						dialogues: [],
						prompt,
						...(rovoChatAIFeatureContext ? { aiFeatureContext: rovoChatAIFeatureContext } : {}),
					},
					source: 'editor',
					...(rovoChatInteractionSource ? { interactionSource: rovoChatInteractionSource } : {}),
				};
				publish(payload);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			editorView,
			editorPluginAIProvider.product,
			rovoChatAIFeatureContext,
			rovoChatInteractionSource,
		],
	);

	const onBeforeSubmitForRovoChat = useCallback(
		(prompt: DocNode) => {
			publishChatNew(prompt);
			cancel();
			return true;
		},
		[publishChatNew, cancel],
	);

	const openInRovoChatConfig = useMemo(
		() => ({ onBeforeSubmit: onBeforeSubmitForRovoChat }),
		[onBeforeSubmitForRovoChat],
	);

	useUserDiscard({
		ref: promptScreenRef,
		onDiscard: cancel,
		lastTriggeredFrom,
		editorView,
		// Keep a background remix update (e.g. a MAUI app) alive when the user clicks
		// in the editor. Escape and the Stop button still cancel.
		disableClickOutside: !!inlineRovoStreamingTargetId,
	});

	// Disable remix/AI entry points while a background remix update is in flight so
	// the user cannot start another remix on top of the running one. Re-enabled on
	// cleanup, which runs when the update completes or aborts (the target id clears
	// and this screen unmounts).
	useEffect(() => {
		if (!inlineRovoStreamingTargetId) {
			return;
		}
		api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
		return () => {
			api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
		};
	}, [api, inlineRovoStreamingTargetId]);

	// Get decoration for modal positioning
	const decoration = findModalDecoration(modalDecorationSet);

	if (!decoration || !modalDecorationSet) {
		return null;
	}

	// RovoInlineChatPrompt has its own internal logic with a loading screen
	// if a session is active, we'll show our own screen to prevent delay between
	// prompt submission and the rovo loading screen appearing
	if (nextSessionId) {
		return null;
	}

	// Use Rovo prompt when onSubmit is available (Rovo-capable products),
	// otherwise fall back to the standard PromptScreen for products without Rovo.
	const hasRovoPrompt = !!editorPluginAIProvider.onSubmit;
	const { PromptEditor } = editorPluginAIProvider;
	const hasFallbackPrompt = !hasRovoPrompt && !!PromptEditor;

	// If neither Rovo prompt nor fallback prompt editor is available, we can't render
	// a prompt screen — fall back to Rovo prompt (which handles its own empty states).
	const renderPromptContent = () => {
		// SmartLink Rovo button: use RovoInlineChatPrompt but intercept submit to
		// open Rovo chat sidebar instead of performing an AI inline edit.
		if (configItem?.openInRovoChat && hasRovoPrompt) {
			return (
				<RovoPromptScreenWithLogic cancel={cancel} openInRovoChatConfig={openInRovoChatConfig} />
			);
		}

		if (hasFallbackPrompt && PromptEditor) {
			return (
				<PromptScreen
					textInput={textInput}
					onTextChange={onTextChange}
					submit={submit}
					cancel={cancel}
					PromptEditor={PromptEditor}
					appearance={appearance}
					isAIFC={isInDocumentStreamingEnabled}
				/>
			);
		}
		return (
			<RovoPromptScreenWithLogic
				cancel={cancel}
				inlineEditContentOverride={configItem?.inlineEditContentOverride}
				preserveLoadingBarMinimizedOnComplete={configItemUsesFreeGen(configItem?.key)}
			/>
		);
	};

	return (
		<div ref={promptScreenRef} css={promptScreenCSS} data-testid="rovo-prompt-session-screen">
			<ModalRegionErrorBoundary inContentComponent onCloseFallback={endExperience}>
				<ModalRegion
					isDirectionalPinningEnabled={isDirectionalPinningEnabled}
					lastTriggeredFrom={lastTriggeredFrom}
					key={modalMountedTimeStamp}
					decoration={decoration}
					editorView={editorView}
					editorRelativeWrapper={wrapperElement?.offsetParent}
					endExperience={endExperience}
					appearance={appearance}
					decorationSet={modalDecorationSet}
					getDeletedWidgets={api?.showDiff?.actions?.getDeletedWidgets}
					isStreaming={isStreaming}
				>
					{renderPromptContent()}
				</ModalRegion>
			</ModalRegionErrorBoundary>
		</div>
	);
}
